import React, { useEffect, useState } from 'react'
import { Link, Redirect } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck'
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes'
import { faCircleNotch } from '@fortawesome/pro-regular-svg-icons/faCircleNotch'
import { useConfig } from '@peracto/peracto-config'

import styled from 'styled-components'
import { Helmet } from 'react-helmet'
import startCase from 'lodash/startCase'

import { Heading, NotFoundMessage, UnauthorisedMessage } from '@peracto/peracto-ui'

import { GET_ONE, useClient } from '@peracto/client'

const TableHeader = styled.h3`
    color: #c8c8c8;
    font-size: 0.8rem;
    padding-bottom: 0.5rem;
    text-transform: uppercase;
    font-weight: 500;
    margin-top: 1rem;
    margin-bottom: 0;
`

const UserGroupsViewContainer = ({ label, children }) => {
    return (
        <div className="form-container">
            <Heading name={label}>
                <div className="!grow !flex !items-center !justify-end">
                    <Link
                        className="btn btn-outline-primary"
                        to="/user-groups"
                        data-testid="back-to-user-groups"
                    >
                        <FontAwesomeIcon icon={faArrowCircleLeft} className="!mr-2" />
                        Back to User Groups
                    </Link>
                </div>
            </Heading>
            {children}
        </div>
    )
}

const UserGroupsView = ({ location: { pathname } }) => {
    const { client } = useClient()
    const [loading, setLoading] = useState(true)
    const [unauthorised, setUnauthorised] = useState(false)
    const [notFound, setNotFound] = useState(false)
    const [redirect, setRedirect] = useState()

    const config = useConfig()
    const { user } = config.get('features', {})

    useEffect(() => {
        if (!user?.userGroups) {
            setRedirect('/')
        }
    }, [user])

    const [userGroup, setUserGroup] = useState()
    const [permissions, setPermissions] = useState()

    useEffect(() => {
        fetchUserGroup()
    }, [])

    const formatPermissions = data => {
        const formattedPermissions = []

        for (const [key, value] of Object.entries(data)) {
            formattedPermissions.push({
                label: startCase(key.replace('ug_', '')),
                value: JSON.stringify(value),
            })
        }

        setPermissions(formattedPermissions)
    }

    const fetchUserGroup = async () => {
        try {
            const { data, response } = await client(GET_ONE, 'user-groups', {
                id: pathname,
            })

            setUserGroup(data)

            formatPermissions(data?.permissions)
            setLoading(false)

            if (response.status === 404) {
                setRedirect('/user-groups')
            }
        } catch (e) {
            console.error(e)

            if (e.status === 403) {
                setUnauthorised(true)
            }

            if (e.status === 404) {
                setNotFound(true)
            }

            setLoading(false)
            setRedirect('/user-groups')
        }
    }

    if (loading) {
        return (
            <UserGroupsViewContainer label="Loading...">
                <div className="card">
                    <div className="card-body !flex !justify-center !items-center !py-12">
                        <FontAwesomeIcon icon={faCircleNotch} size="4x" spin />
                    </div>
                </div>
            </UserGroupsViewContainer>
        )
    }

    if (unauthorised) {
        return <UnauthorisedMessage />
    }

    if (notFound) {
        return (
            <NotFoundMessage
                url="/user-groups"
                message="The user group you're looking for could not be found"
                buttonLabel="Go to User Groups"
            />
        )
    }

    return (
        <>
            <Helmet>
                <title>{userGroup?.name || 'User Group'} | User Groups | Peracto</title>
            </Helmet>
            <UserGroupsViewContainer label={userGroup?.name}>
                {redirect && <Redirect to={redirect} />}

                <div className="card !mb-4">
                    <div className="card-body">
                        <TableHeader>Group Information</TableHeader>
                        <table className="table table-sm">
                            <colgroup>
                                <col width="30%" />
                                <col width="70%" />
                            </colgroup>
                            <tbody>
                                <tr>
                                    <td>Inherits Default Permissions</td>
                                    <td data-testid="user-group-inherits-default-permissions">
                                        <FontAwesomeIcon
                                            icon={
                                                userGroup?.inheritsDefaultPermissions
                                                    ? faCheck
                                                    : faTimes
                                            }
                                            className={
                                                userGroup?.inheritsDefaultPermissions
                                                    ? '!text-success'
                                                    : '!text-danger'
                                            }
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td>Types</td>
                                    <td data-testid="user-group-types">
                                        {userGroup?.types?.length > 0
                                            ? userGroup.types.join(', ')
                                            : 'None'}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="card">
                    <div className="card-body">
                        {permissions?.length > 0 && (
                            <>
                                <TableHeader>Permissions</TableHeader>

                                <table className="table table-sm">
                                    <colgroup>
                                        <col width="30%" />
                                        <col width="70%" />
                                    </colgroup>

                                    <tbody>
                                        {permissions.map((permission, idx) => (
                                            <tr key={idx}>
                                                <td data-testid={`row-${idx}-label`}>
                                                    {permission.label}
                                                </td>
                                                <td data-testid={`row-${idx}-value`}>
                                                    {permission.value}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>
                </div>
            </UserGroupsViewContainer>
        </>
    )
}

export default UserGroupsView
